const API = process.env.REACT_APP_BACKEND_URL || '';

export interface ChargeScore {
  pseudo: string;
  tempsSec: number;
  penalitesSec: number;
  motsTrouves: number;
  jeton: string;
}

export interface ReponseEnvoi {
  ok: boolean;
  conflit?: boolean;
  suggestion?: string;
  rang?: number;
  statut?: string;
}

export async function envoyerScore(charge: ChargeScore, timeoutMs = 4000): Promise<ReponseEnvoi> {
  const ctrl = new AbortController();
  const timer = window.setTimeout(() => ctrl.abort(), timeoutMs);
  try {
    const r = await fetch(`${API}/api/score`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(charge),
      signal: ctrl.signal,
    });
    if (r.status === 409) {
      const d = await r.json().catch(() => null);
      return { ok: false, conflit: true, suggestion: d?.detail?.suggestion };
    }
    if (!r.ok) return { ok: false };
    const d = await r.json();
    return { ok: true, rang: d.rang, statut: d.statut };
  } catch {
    return { ok: false };
  } finally {
    window.clearTimeout(timer);
  }
}

export interface LigneClassement {
  rang: number;
  pseudo: string;
  tempsSec: number;
  penalitesSec: number;
  motsTrouves: number;
  jeton?: string;
  horodatage: string;
}

export interface DonneesClassement {
  scores: LigneClassement[];
  total: number;
}

export async function recupererClassement(): Promise<DonneesClassement | null> {
  try {
    const r = await fetch(`${API}/api/leaderboard`);
    if (!r.ok) return null;
    return await r.json();
  } catch {
    return null;
  }
}

export function lienIndicesGreffier(code: string): string {
  return `${API}/api/greffier/indices?code=${encodeURIComponent(code)}`;
}

export async function recupererEtatJeu(): Promise<boolean | null> {
  const ctrl = new AbortController();
  const t = window.setTimeout(() => ctrl.abort(), 3000);
  try {
    const r = await fetch(`${API}/api/etat-jeu`, { signal: ctrl.signal });
    if (!r.ok) return null;
    const d = await r.json();
    return !!d.actif;
  } catch {
    return null;
  } finally {
    window.clearTimeout(t);
  }
}

export async function verifierCodeGreffier(code: string): Promise<boolean> {
  try {
    const r = await fetch(`${API}/api/greffier/ping`, { headers: { 'X-Greffier-Code': code } });
    return r.ok;
  } catch {
    return false;
  }
}

export interface ChargeGreffier {
  pseudo: string;
  tempsSec: number;
  penalitesSec: number;
  motsTrouves: number;
  jeton?: string;
}

export async function ajouterScoreGreffier(
  code: string,
  charge: ChargeGreffier
): Promise<{ ok: boolean; conflit?: boolean; suggestion?: string; statut?: string; rang?: number }> {
  try {
    const r = await fetch(`${API}/api/greffier/score`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-Greffier-Code': code },
      body: JSON.stringify(charge),
    });
    if (r.status === 409) {
      const d = await r.json().catch(() => null);
      return { ok: false, conflit: true, suggestion: d?.detail?.suggestion };
    }
    if (!r.ok) return { ok: false };
    const d = await r.json();
    return { ok: true, statut: d.statut, rang: d.rang };
  } catch {
    return { ok: false };
  }
}

export async function supprimerScoreGreffier(code: string, jeton: string): Promise<boolean> {
  try {
    const r = await fetch(`${API}/api/greffier/score/${encodeURIComponent(jeton)}`, {
      method: 'DELETE',
      headers: { 'X-Greffier-Code': code },
    });
    return r.ok;
  } catch {
    return false;
  }
}

export async function setEtatJeuGreffier(code: string, actif: boolean): Promise<boolean> {
  try {
    const r = await fetch(`${API}/api/greffier/etat-jeu`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-Greffier-Code': code },
      body: JSON.stringify({ actif }),
    });
    return r.ok;
  } catch {
    return false;
  }
}

export async function viderClassementGreffier(code: string): Promise<number | null> {
  try {
    const r = await fetch(`${API}/api/greffier/scores`, {
      method: 'DELETE',
      headers: { 'X-Greffier-Code': code },
    });
    if (!r.ok) return null;
    const d = await r.json();
    return d.supprimes ?? 0;
  } catch {
    return null;
  }
}

export interface PhotoEnLigne {
  id: string;
  legende: string;
  url: string;
}

export async function recupererBestOfEnLigne(): Promise<PhotoEnLigne[]> {
  try {
    const r = await fetch(`${API}/api/bestof`);
    if (!r.ok) return [];
    const d = await r.json();
    return (d.photos || []).map((p: PhotoEnLigne) => ({ ...p, url: `${API}${p.url}` }));
  } catch {
    return [];
  }
}

export async function televerserBestOf(code: string, fichier: File, legende: string): Promise<PhotoEnLigne | null> {
  try {
    const forme = new FormData();
    forme.append('fichier', fichier);
    forme.append('legende', legende);
    const r = await fetch(`${API}/api/greffier/bestof`, {
      method: 'POST',
      headers: { 'X-Greffier-Code': code },
      body: forme,
    });
    if (!r.ok) return null;
    const d = await r.json();
    return { ...d, url: `${API}${d.url}` };
  } catch {
    return null;
  }
}

export async function supprimerBestOf(code: string, id: string): Promise<boolean> {
  try {
    const r = await fetch(`${API}/api/greffier/bestof/${encodeURIComponent(id)}`, {
      method: 'DELETE',
      headers: { 'X-Greffier-Code': code },
    });
    return r.ok;
  } catch {
    return false;
  }
}
